import { env } from '@/lib/env'
import { assetUrl } from './model'
import type { TenantAddress } from './types'

/*
 * P12 — the document head at a company's address: its icon (the tab's and
 * the home screen's) and its colour as the browser's theme colour. Nothing
 * of the company's reaches the head at any other address; "nothing at this
 * address" asks search engines not to index it.
 */

const HEX = /^#[0-9a-f]{6}$/i

function headLink(rel: string): HTMLLinkElement {
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (link === null) {
    link = document.createElement('link')
    link.rel = rel
    document.head.appendChild(link)
  }
  return link
}

function headMeta(name: string): HTMLMetaElement {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  if (meta === null) {
    meta = document.createElement('meta')
    meta.name = name
    document.head.appendChild(meta)
  }
  return meta
}

/** The company's favicon, touch icon and theme colour, where it has them. */
export function applyAddressHead(address: TenantAddress): void {
  const icons = address.company.icons ?? null
  const small = icons?.['32'] ?? icons?.['192'] ?? null

  if (small !== null) {
    const link = headLink('icon')
    link.type = 'image/png'
    link.href = assetUrl(small, env.apiUrl)
  }

  const touch = icons?.['180'] ?? null
  if (touch !== null) headLink('apple-touch-icon').href = assetUrl(touch, env.apiUrl)

  const accent = address.brand?.ui?.accent ?? null
  if (accent !== null && HEX.test(accent)) headMeta('theme-color').content = accent
}

/** `<meta name="robots" content="noindex">` while a page is shown; the returned function puts things back. */
export function markNoIndex(): () => void {
  const meta = headMeta('robots')
  const before = meta.getAttribute('content')
  meta.content = 'noindex'

  return () => {
    if (before === null || before === '') meta.remove()
    else meta.content = before
  }
}
